/* eslint-disable @typescript-eslint/consistent-type-definitions -- the payload types back a Zero json() column, which needs type aliases; interfaces lack an implicit index signature. */
// Shared by the recommendations engine (packages/api) and the Agent page
// (apps/app). Keep it free of server-only imports.

export type AgentRecommendationType =
	| "CONTENT"
	| "GUIDANCE"
	| "DATA"
	| "ACTION"
	| "CONTRADICTION"

export type AgentRecommendationStatus =
	| "OPEN"
	| "ACCEPTED"
	| "DISMISSED"
	| "EXPIRED"

export type AgentEvidenceOutcome =
	| "handed_off"
	| "teammate_answered"
	| "wrong_answer"
	| "no_answer"

export type AgentRecommendationEvidence = {
	threadId: string
	question: string
	outcome: AgentEvidenceOutcome
	// What the agent told the customer, empty when it never replied.
	agentAnswer?: string
	// The reply the agent could give once the change or connector exists.
	improvedAnswer?: string
}

export const AGENT_EXAMPLE_ACTION_TYPES = [
	"change_plan",
	"extend_trial",
	"cancel_subscription",
	"apply_promotion_code",
	"remove_promotion_code",
] as const

// An action the agent could propose once connected, drawn as an approval card.
export type AgentExampleAction = {
	actionType: (typeof AGENT_EXAMPLE_ACTION_TYPES)[number]
	summary: string
	before: string
	after: string
}

export type AgentGuidanceChange = {
	before: string
	after: string
}

// The two ways the support agent reaches a workspace's own systems.
export const AGENT_CONNECTOR_KINDS = ["custom_connector", "postgres"] as const
export type AgentConnectorKind = (typeof AGENT_CONNECTOR_KINDS)[number]

export const AGENT_CONNECTOR_SETUP_PATHS: Record<AgentConnectorKind, string> = {
	custom_connector: "/settings/custom-connectors",
	postgres: "/settings/integrations/postgres",
}

export type AgentCapabilityGuide = {
	need: string
	suggestion: string
	exampleQuestions: string[]
	exampleAction?: AgentExampleAction
	// Absent on cards written before the review picked a connector.
	connector?: AgentConnectorKind
	// The system to connect, named only when the conversations name it, such as "Stripe".
	system?: string
}

// The Linear issue an engineering recommendation became. `issueId` is the
// Productlane Issue row, set once the threads are linked.
export type AgentLinkedIssue = {
	linearIssueId: string
	identifier: string
	url: string
	statusName: string | null
	issueId: string | null
}

// Data and action gaps both need an engineer to connect a system, so the Agent
// page shows them as one Engineering type. New ones are stored as DATA.
export const ENGINEERING_RECOMMENDATION_TYPES: readonly AgentRecommendationType[] =
	["DATA", "ACTION"]

export function isEngineeringRecommendation(
	type: AgentRecommendationType,
): boolean {
	return ENGINEERING_RECOMMENDATION_TYPES.includes(type)
}

export type AgentRecommendationPayload = {
	// The agent's opening line, written for this gap so no two ideas read alike.
	intro?: string
	evidence?: AgentRecommendationEvidence[]
	guidance?: AgentGuidanceChange
	guide?: AgentCapabilityGuide
	linearIssue?: AgentLinkedIssue
	// Where the fix's answer came from: the repository when no teammate had
	// answered, else a teammate's reply. Absent on cards built before it was stored.
	learnedFrom?: "code" | "teammate"
	// ISO date of the oldest conversation behind it, for the "since" line.
	since?: string
	// One key per asker (contact id, or the thread for an anonymous chat), so
	// a customer who writes in twice is counted once.
	askers?: string[]
	// Threads whose example answers were already generated, so an example the
	// change cannot answer is not regenerated on every open.
	exampleAnswersTried?: string[]
	measured?: AgentRecommendationMeasured
}

export type AgentRecommendationMeasured = {
	resolved: number
	threadIds: string[]
}

const MAX_ASKERS = 500

export function mergeAskers(
	existing: readonly string[] | undefined,
	incoming: ReadonlyArray<{ contactId: string | null; threadId: string }>,
): string[] {
	const out = new Set(existing ?? [])
	for (const c of incoming) out.add(c.contactId ?? `thread:${c.threadId}`)
	return [...out].slice(0, MAX_ASKERS)
}

export const AGENT_RECOMMENDATION_WINDOW_DAYS = 90
export const AGENT_RECOMMENDATION_EXPIRY_DAYS = 28
// New review cards wait until the team has worked the open ones down below this.
// Drafts sent through the API or MCP are not cards, so this never holds them back.
export const MAX_OPEN_AGENT_RECOMMENDATIONS = 20
export const MAX_EVIDENCE_PER_RECOMMENDATION = 5

function exampleNeedsAnswers(
	item: AgentRecommendationEvidence,
	tried: ReadonlySet<string>,
): boolean {
	return !(item.agentAnswer && item.improvedAnswer) && !tried.has(item.threadId)
}

/** The examples the slider shows that still lack an answer nobody has tried yet. */
export function examplesNeedingAnswers(
	payload: AgentRecommendationPayload | null | undefined,
): AgentRecommendationEvidence[] {
	const tried = new Set(payload?.exampleAnswersTried ?? [])
	return (payload?.evidence ?? [])
		.slice(0, MAX_EVIDENCE_PER_RECOMMENDATION)
		.filter((item) => exampleNeedsAnswers(item, tried))
}

export type ExampleAnswers = Readonly<
	Record<string, { agentAnswer?: string; improvedAnswer?: string }>
>

/** Threads a fresh build answered; the rest stay open for one backfill. */
export function answeredExampleThreadIds(
	evidence: readonly { threadId: string }[],
	answers: Readonly<Record<string, string>> | undefined,
): string[] {
	return evidence.filter((e) => answers?.[e.threadId]).map((e) => e.threadId)
}

/**
 * Fills missing answers only and marks the threads as tried; every other field,
 * and any answer already stored, stays as it is.
 */
export function mergeExampleAnswers(
	payload: AgentRecommendationPayload,
	answers: ExampleAnswers,
	triedThreadIds: readonly string[],
): AgentRecommendationPayload {
	const evidence = payload.evidence?.map((item) => {
		const found = answers[item.threadId]
		const agentAnswer = item.agentAnswer || found?.agentAnswer?.trim()
		const improvedAnswer = item.improvedAnswer || found?.improvedAnswer?.trim()
		return {
			...item,
			...(agentAnswer ? { agentAnswer } : {}),
			...(improvedAnswer ? { improvedAnswer } : {}),
		}
	})
	const tried = [
		...new Set([...(payload.exampleAnswersTried ?? []), ...triedThreadIds]),
	].slice(-50)
	return {
		...payload,
		...(evidence ? { evidence } : {}),
		exampleAnswersTried: tried,
	}
}

// Our own workspace plus early-access customers: the only workspaces with the Agent
// page and its daily recommendations engine for now.
export const AGENT_PAGE_WORKSPACE_IDS: readonly string[] = [
	"922eb553-8dc5-4a00-a16b-01735334be53", // Productlane
	"3eabcf8f-5478-4c09-9fb0-10259a9a8bca", // premote
	"b15d0788-d980-45ab-a858-a3a3e6a0174d", // Wunder Mobility
	"2657a001-3767-4888-a2ec-ac5fa61b9df4", // Medialister
	"989d5b36-df94-4e5d-9876-56ad2a5109cc", // Tellet
	"8599440c-0055-4463-b8e1-8f90476142b6", // Linearity
	"0e05ccb2-f2eb-40d5-80c6-4056d7ff9be8", // sell & pick
	"1dc2b53e-b5ab-448c-98ab-11c7182f0b48", // CheckSammy
	"1783c074-82fd-4215-a47a-298da9c37a01", // alago
	"d3d43bbe-b355-462b-aaf8-679ea0f36a2f", // Mergepoint
	"56cdb889-ab63-4c28-8c8a-a5fb85a4212e", // Primmo
]

// Workspaces created from this day on open on the Agent page, so they get it too.
export const AGENT_PAGE_NEW_WORKSPACES_FROM = new Date("2026-10-28T00:00:00Z")

export function isAgentEngineAvailable(
	workspaceId: string | null | undefined,
	{
		isDevelopment = false,
		createdAt,
	}: { isDevelopment?: boolean; createdAt?: Date | number | null } = {},
): boolean {
	if (isDevelopment) return true
	if (!workspaceId) return false
	if (AGENT_PAGE_WORKSPACE_IDS.includes(workspaceId)) return true
	return (
		createdAt != null &&
		new Date(createdAt).getTime() >= AGENT_PAGE_NEW_WORKSPACES_FROM.getTime()
	)
}

// An engineering gap has no text to check, so it is assumed to recover about
// half of its conversations once the connector exists.
const UNCHECKED_SUCCESS_RATE = 0.5
const MIN_WINDOW_DAYS = 14

export function estimateImpactPerMonth({
	conversationCount,
	windowDays,
	rerunAnswered,
	rerunTotal,
}: {
	conversationCount: number
	windowDays: number
	rerunAnswered?: number | null
	rerunTotal?: number | null
}): number {
	if (conversationCount <= 0) return 0
	// A short window would project a one-week burst into a monthly figure.
	const days = Math.min(
		AGENT_RECOMMENDATION_WINDOW_DAYS,
		Math.max(MIN_WINDOW_DAYS, windowDays),
	)
	const successRate =
		rerunTotal && rerunTotal > 0
			? Math.min(1, Math.max(0, (rerunAnswered ?? 0) / rerunTotal))
			: UNCHECKED_SUCCESS_RATE
	return Math.max(
		1,
		Math.round(((conversationCount * 30) / days) * successRate),
	)
}

export function agentRecommendationFingerprint(
	type: AgentRecommendationType,
	key: string,
): string {
	const slug = key
		.toLowerCase()
		.normalize("NFKD")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 80)
	return `${type.toLowerCase()}:${slug}`
}

export type GuidanceDiffLine =
	| { type: "same"; text: string }
	| { type: "removed"; text: string }
	| { type: "added"; text: string }

// Line-level LCS diff. Guidance is a short text field, so the quadratic table
// stays tiny.
export function diffGuidanceLines(
	before: string,
	after: string,
): GuidanceDiffLine[] {
	const a = splitLines(before)
	const b = splitLines(after)
	const table: number[][] = Array.from({ length: a.length + 1 }, () =>
		new Array<number>(b.length + 1).fill(0),
	)
	for (let i = a.length - 1; i >= 0; i--) {
		for (let j = b.length - 1; j >= 0; j--) {
			table[i]![j] =
				a[i] === b[j]
					? table[i + 1]![j + 1]! + 1
					: Math.max(table[i + 1]![j]!, table[i]![j + 1]!)
		}
	}
	const out: GuidanceDiffLine[] = []
	let i = 0
	let j = 0
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			out.push({ type: "same", text: a[i]! })
			i++
			j++
		} else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
			out.push({ type: "removed", text: a[i]! })
			i++
		} else {
			out.push({ type: "added", text: b[j]! })
			j++
		}
	}
	while (i < a.length) out.push({ type: "removed", text: a[i++]! })
	while (j < b.length) out.push({ type: "added", text: b[j++]! })
	return out
}

/** Only what the change adds or removes; the guidance it leaves alone stays out of the card. */
export function changedGuidanceLines(
	before: string,
	after: string,
): GuidanceDiffLine[] {
	return diffGuidanceLines(before, after).filter((line) => line.type !== "same")
}

/**
 * Applies a guidance change onto guidance that moved on since it was written,
 * which accepting several at once always causes. Only a change that adds
 * lines can move; one that rewrites or removes a line returns null.
 */
export function rebaseGuidance(
	before: string,
	after: string,
	current: string,
): string | null {
	if (current.trim() === before.trim()) return after
	const changed = changedGuidanceLines(before, after)
	if (changed.some((line) => line.type === "removed")) return null
	const existing = new Set(splitLines(current))
	const added = changed
		.map((line) => line.text)
		.filter((text) => !existing.has(text))
	if (added.length === 0) return current
	return [current.trimEnd(), ...added].filter(Boolean).join("\n\n")
}

export const MAX_GUIDANCE_LINES_CHANGED = 2

// A drafted fix may change or add two lines. A bigger diff rewrote the guidance
// or pasted the drafting prompt's own rules into it.
export function isSmallGuidanceEdit(before: string, after: string): boolean {
	const lines = diffGuidanceLines(before, after)
	const added = lines.filter((line) => line.type === "added").length
	const removed = lines.filter((line) => line.type === "removed").length
	return (
		added <= MAX_GUIDANCE_LINES_CHANGED && removed <= MAX_GUIDANCE_LINES_CHANGED
	)
}

// Every guidance card is drafted against the guidance of its day, so once one
// is accepted the others replay their line edits onto the new text. Returns
// null when a line the card removes or rewrites is no longer there.
export function rebaseGuidanceChange(
	before: string,
	after: string,
	current: string,
): string | null {
	if (current.trim() === before.trim()) return after
	const ops = diffGuidanceLines(before, after)
	const lines = current.split(/\r?\n/)
	const keys = lines.map((line) => line.trimEnd())
	const present = new Set(keys)
	const removed = new Set<number>()
	const inserts = new Map<number, string[]>()
	const appended: string[] = []
	let lastAnchor = -1
	ops.forEach((op, index) => {
		if (op.type !== "added") lastAnchor = index
	})
	let pos = 0
	const find = (text: string) => {
		for (let k = pos; k < keys.length; k++) {
			if (keys[k] === text && !removed.has(k)) return k
		}
		return -1
	}
	for (const [index, op] of ops.entries()) {
		if (op.type === "same") {
			const k = find(op.text)
			if (k >= 0) pos = k + 1
		} else if (op.type === "removed") {
			const k = find(op.text)
			if (k < 0) return null
			removed.add(k)
			pos = k + 1
		} else if (present.has(op.text)) {
			continue
		} else if (index > lastAnchor) {
			appended.push(op.text)
		} else {
			inserts.set(pos, [...(inserts.get(pos) ?? []), op.text])
		}
	}
	const out: string[] = []
	for (let k = 0; k <= lines.length; k++) {
		out.push(...(inserts.get(k) ?? []))
		if (k < lines.length && !removed.has(k)) out.push(lines[k]!)
	}
	const text = out.join("\n").trimEnd()
	if (appended.length === 0) return text
	const gap = /\n[ \t]*\n/.test(after.trim()) ? "\n\n" : "\n"
	return text ? `${text}${gap}${appended.join(gap)}` : appended.join(gap)
}

function splitLines(text: string): string[] {
	return text
		.split(/\r?\n/)
		.map((line) => line.trimEnd())
		.filter((line) => line.trim().length > 0)
}
